<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useStore } from 'dashboard/composables/store';
import VariableList from 'dashboard/components/widgets/conversation/VariableList.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  templateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const MEDIA_UPLOAD_RULES = {
  IMAGE: {
    accept: 'image/jpeg,image/png',
    maxSize: 5 * 1024 * 1024,
    types: ['image/jpeg', 'image/png'],
    extensions: ['.jpg', '.jpeg', '.png'],
    typeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_IMAGE_TYPE_ERROR',
    sizeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_IMAGE_SIZE_ERROR',
  },
  VIDEO: {
    accept: 'video/mp4',
    maxSize: 16 * 1024 * 1024,
    types: ['video/mp4'],
    extensions: ['.mp4'],
    typeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_VIDEO_TYPE_ERROR',
    sizeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_VIDEO_SIZE_ERROR',
  },
  DOCUMENT: {
    accept: 'application/pdf',
    maxSize: 100 * 1024 * 1024,
    types: ['application/pdf'],
    extensions: ['.pdf'],
    typeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_DOCUMENT_TYPE_ERROR',
    sizeError: 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_DOCUMENT_SIZE_ERROR',
  },
};

export default {
  components: {
    VariableList,
  },
  props: {
    template: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const processedParams = ref({});
    const mediaUploadInput = ref(null);
    const isUploadingMedia = ref(false);
    const mediaUploadError = ref('');
    const uploadedMediaFileName = ref('');
    const activeVariableTarget = ref(null);

    const headerComponent = computed(() =>
      findComponentByType(props.template, COMPONENT_TYPES.HEADER)
    );

    const bodyComponent = computed(() =>
      findComponentByType(props.template, COMPONENT_TYPES.BODY)
    );

    const bodyText = computed(() => bodyComponent.value?.text || '');
    const headerText = computed(() => headerComponent.value?.text || '');

    const hasMediaHeader = computed(() =>
      MEDIA_FORMATS.includes(headerComponent.value?.format)
    );

    const mediaTypeLabelKey = computed(() => {
      const format = headerComponent.value?.format || 'IMAGE';
      return `WHATSAPP_TEMPLATES.ADMIN.BUILDER.HEADER.TYPES.${format}`;
    });

    const acceptedMediaTypes = computed(() => {
      const format = headerComponent.value?.format;
      return MEDIA_UPLOAD_RULES[format]?.accept || '*/*';
    });

    const hasBodyVariables = computed(
      () => templateVariables(bodyText.value).length > 0
    );

    const hasHeaderVariables = computed(
      () =>
        headerComponent.value?.format === 'TEXT' &&
        templateVariables(headerText.value).length > 0
    );

    const hasVariables = computed(() => {
      return hasBodyVariables.value || hasHeaderVariables.value;
    });

    const headerTextParams = computed(() => {
      const header = processedParams.value.header || {};
      return Object.keys(header).reduce((params, key) => {
        if (!['media_url', 'media_type'].includes(key)) {
          params[key] = header[key];
        }
        return params;
      }, {});
    });

    const hasButtonParameters = computed(() =>
      Boolean(
        processedParams.value.buttons?.some(
          button => button?.parameter !== undefined
        )
      )
    );

    const renderedTemplate = computed(() => {
      return replaceTemplateVariables(bodyText.value, processedParams.value);
    });

    const isFormInvalid = computed(() => {
      if (isUploadingMedia.value) return true;
      if (
        !hasVariables.value &&
        !hasMediaHeader.value &&
        !hasButtonParameters.value
      )
        return false;

      if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
        return true;
      }

      if (hasHeaderVariables.value) {
        const headerKeys = Object.keys(headerTextParams.value);
        const hasEmptyHeaderVariable =
          !headerKeys.length ||
          headerKeys.some(key => !processedParams.value.header?.[key]);
        if (hasEmptyHeaderVariable) return true;
      }

      if (hasVariables.value && processedParams.value.body) {
        const hasEmptyBodyVariable = Object.values(
          processedParams.value.body
        ).some(value => !value);
        if (hasEmptyBodyVariable) return true;
      }

      if (processedParams.value.buttons) {
        const hasEmptyButtonParameter = processedParams.value.buttons.some(
          button => button && !button.parameter
        );
        if (hasEmptyButtonParameter) return true;
      }

      return false;
    });

    const v$ = useVuelidate(
      {
        processedParams: {
          requiredIfKeysPresent: requiredIf(hasVariables),
          allKeysRequired,
        },
      },
      { processedParams }
    );

    const initializeTemplateParameters = () => {
      processedParams.value = buildTemplateParameters(
        props.template,
        hasMediaHeader.value
      );
      if (hasMediaHeader.value && props.template.local_header_media_url) {
        processedParams.value.header.media_url = normalizeUploadedMediaUrl(
          props.template.local_header_media_url
        );
      }
    };

    const setMediaUrl = (value, uploadedFileName = '') => {
      if (!processedParams.value.header) processedParams.value.header = {};
      processedParams.value.header.media_url = value;
      mediaUploadError.value = '';
      uploadedMediaFileName.value = uploadedFileName;
    };

    const resetMediaUploadInput = () => {
      if (mediaUploadInput.value) {
        mediaUploadInput.value.value = '';
      }
    };

    const triggerMediaUpload = () => {
      mediaUploadError.value = '';
      mediaUploadInput.value?.click();
    };

    const fileMatchesRule = (file, rule) => {
      const fileName = file.name?.toLowerCase() || '';
      return (
        rule.types.includes(file.type) ||
        rule.extensions.some(extension => fileName.endsWith(extension))
      );
    };

    const getMediaFileValidationError = file => {
      if (!file) return 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_INVALID';

      const format = headerComponent.value?.format;
      const rule = MEDIA_UPLOAD_RULES[format];
      if (!rule) return 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_INVALID';

      if (!fileMatchesRule(file, rule)) {
        return rule.typeError;
      }
      if (file.size > rule.maxSize) {
        return rule.sizeError;
      }
      return '';
    };

    const normalizeUploadedMediaUrl = fileUrl => {
      if (!fileUrl || !fileUrl.startsWith('/')) return fileUrl;

      return `${window.location.origin}${fileUrl}`;
    };

    const onMediaFileSelected = async event => {
      const [file] = Array.from(event.target.files || []);
      if (!file) return;

      const validationError = getMediaFileValidationError(file);
      if (validationError) {
        mediaUploadError.value = validationError;
        resetMediaUploadInput();
        return;
      }

      isUploadingMedia.value = true;
      mediaUploadError.value = '';

      try {
        const { fileUrl } = await uploadFile(file);
        setMediaUrl(normalizeUploadedMediaUrl(fileUrl), file.name);
      } catch (error) {
        mediaUploadError.value = 'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR';
      } finally {
        isUploadingMedia.value = false;
        resetMediaUploadInput();
      }
    };

    const resetTemplate = () => {
      emit('resetTemplate');
    };

    const variableTargetId = (section, key) => `${section}:${key}`;

    const isVariableMenuOpen = (section, key) =>
      activeVariableTarget.value?.id === variableTargetId(section, key);

    const toggleVariableMenu = (section, key) => {
      const id = variableTargetId(section, key);
      activeVariableTarget.value =
        activeVariableTarget.value?.id === id ? null : { id, section, key };
    };

    const insertContactVariable = variableKey => {
      const target = activeVariableTarget.value;
      if (!target) return;

      const value = `{{${variableKey}}}`;
      if (target.section === 'buttons') {
        processedParams.value.buttons[target.key].parameter = value;
      } else {
        processedParams.value[target.section][target.key] = value;
      }
      activeVariableTarget.value = null;
    };

    const sendMessage = () => {
      v$.value.$touch();
      if (v$.value.$invalid || isFormInvalid.value) return;

      const { name, category, language, namespace } = props.template;

      const payload = {
        message: renderedTemplate.value,
        templateParams: {
          name,
          category,
          language,
          namespace,
          processed_params: processedParams.value,
        },
      };
      emit('sendMessage', payload);
    };

    onMounted(() => {
      initializeTemplateParameters();
      store?.dispatch?.('attributes/get');
    });

    watch(
      () => props.template,
      () => {
        initializeTemplateParameters();
        activeVariableTarget.value = null;
        mediaUploadError.value = '';
        uploadedMediaFileName.value = '';
        resetMediaUploadInput();
        v$.value.$reset();
      },
      { deep: true }
    );

    return {
      processedParams,
      mediaUploadInput,
      isUploadingMedia,
      mediaUploadError,
      uploadedMediaFileName,
      activeVariableTarget,
      hasVariables,
      hasHeaderVariables,
      hasButtonParameters,
      headerTextParams,
      hasMediaHeader,
      mediaTypeLabelKey,
      acceptedMediaTypes,
      renderedTemplate,
      v$,
      isFormInvalid,
      triggerMediaUpload,
      onMediaFileSelected,
      isVariableMenuOpen,
      toggleVariableMenu,
      insertContactVariable,
      resetTemplate,
      sendMessage,
      DEFAULT_LANGUAGE,
      DEFAULT_CATEGORY,
    };
  },
  methods: {
    getCategoryLabel(category) {
      const value = category || DEFAULT_CATEGORY;
      const key = `WHATSAPP_TEMPLATES.PICKER.FILTERS.${value}`;
      const translated = this.$t(key);
      return translated === key ? value : translated;
    },
    getButtonLabel(index) {
      return this.$t('WHATSAPP_TEMPLATES.PARSER.BUTTON_LABEL', {
        number: index + 1,
      });
    },
  },
};
</script>

<template>
  <div class="w-full">
    <div class="template-preview-container">
      <div class="flex justify-between items-center mb-2">
        <h3 class="text-sm font-medium text-body">
          {{ template.name }}
        </h3>
        <span class="text-xs text-subtle">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE') }}:
          {{ template.language || DEFAULT_LANGUAGE }}
        </span>
      </div>

      <textarea
        v-model="renderedTemplate"
        rows="4"
        readonly
        class="template-input"
      />

      <div class="text-xs text-subtle mt-1">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.CATEGORY') }}:
        {{ getCategoryLabel(template.category || DEFAULT_CATEGORY) }}
      </div>
    </div>

    <div
      v-if="hasVariables || hasMediaHeader || hasButtonParameters"
      class="template__variables-container"
    >
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="variables-label">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_FILE_LABEL', {
              type: $t(mediaTypeLabelKey).toLowerCase(),
            })
          }}
        </p>
        <div class="template__media-row">
          <woot-button
            type="button"
            variant="smooth"
            color-scheme="secondary"
            :is-loading="isUploadingMedia"
            :is-disabled="isUploadingMedia"
            @click="triggerMediaUpload"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_LABEL') }}
          </woot-button>
          <input
            ref="mediaUploadInput"
            type="file"
            class="hidden"
            :accept="acceptedMediaTypes"
            @change="onMediaFileSelected"
          />
        </div>
        <p class="media-helper">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_HELP') }}
        </p>
        <p v-if="uploadedMediaFileName" class="media-success">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_SUCCESS', {
              fileName: uploadedMediaFileName,
            })
          }}
        </p>
        <p v-if="mediaUploadError" class="media-error">
          {{ $t(mediaUploadError) }}
        </p>
      </div>

      <div v-if="hasHeaderVariables && processedParams.header" class="mb-4">
        <p class="variables-label">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(_, key) in headerTextParams"
          :key="`header-${key}`"
          class="template__variable-item"
        >
          <span class="variable-label">{{ key }}</span>
          <div class="variable-control">
            <woot-input
              v-model="processedParams.header[key]"
              type="text"
              class="variable-input"
              :styles="{ marginBottom: 0 }"
            />
            <button
              type="button"
              class="variable-picker-button"
              :title="$t('WHATSAPP_TEMPLATES.PARSER.CONTACT_VARIABLE_LABEL')"
              @click="toggleVariableMenu('header', key)"
            >
              <fluent-icon icon="code" size="16" />
            </button>
            <VariableList
              v-if="isVariableMenuOpen('header', key)"
              :allowed-prefixes="['contact.']"
              @click="insertContactVariable"
            />
          </div>
        </div>
      </div>

      <div v-if="processedParams.body">
        <p class="variables-label">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="template__variable-item"
        >
          <span class="variable-label">{{ key }}</span>
          <div class="variable-control">
            <woot-input
              v-model="processedParams.body[key]"
              type="text"
              class="variable-input"
              :styles="{ marginBottom: 0 }"
            />
            <button
              type="button"
              class="variable-picker-button"
              :title="$t('WHATSAPP_TEMPLATES.PARSER.CONTACT_VARIABLE_LABEL')"
              @click="toggleVariableMenu('body', key)"
            >
              <fluent-icon icon="code" size="16" />
            </button>
            <VariableList
              v-if="isVariableMenuOpen('body', key)"
              :allowed-prefixes="['contact.']"
              @click="insertContactVariable"
            />
          </div>
        </div>
      </div>

      <div v-if="processedParams.buttons && processedParams.buttons.length > 0">
        <p class="variables-label mt-4">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="template__variable-item"
        >
          <span class="variable-label">{{ getButtonLabel(index) }}</span>
          <div class="variable-control">
            <woot-input
              v-model="processedParams.buttons[index].parameter"
              type="text"
              class="variable-input"
              :styles="{ marginBottom: 0 }"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER_PLACEHOLDER')
              "
            />
            <button
              type="button"
              class="variable-picker-button"
              :title="$t('WHATSAPP_TEMPLATES.PARSER.CONTACT_VARIABLE_LABEL')"
              @click="toggleVariableMenu('buttons', index)"
            >
              <fluent-icon icon="code" size="16" />
            </button>
            <VariableList
              v-if="isVariableMenuOpen('buttons', index)"
              :allowed-prefixes="['contact.']"
              @click="insertContactVariable"
            />
          </div>
        </div>
      </div>

      <p v-if="v$.$dirty && (v$.$invalid || isFormInvalid)" class="error">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <footer>
      <woot-button variant="smooth" @click="resetTemplate">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </woot-button>
      <woot-button type="button" :disabled="isFormInvalid" @click="sendMessage">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL') }}
      </woot-button>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.template-preview-container {
  @apply mb-4 p-4 rounded-lg bg-surface-sunken border border-line-faint;
}

.template__variables-container {
  @apply p-2.5;
}

.variables-label {
  @apply text-sm font-semibold mb-2.5 text-muted;
}

.template__variable-item {
  @apply items-center flex mb-2.5;

  .variable-label {
    @apply bg-surface-hover text-muted inline-block rounded-md text-xs py-2.5 px-4 min-w-[3rem] text-center font-medium;
  }
}

.variable-control {
  @apply relative flex flex-1 items-start gap-1 ml-2.5;

  .variable-input {
    @apply flex-1 text-sm;
  }
}

.variable-picker-button {
  @apply flex items-center justify-center shrink-0 w-10 h-10 rounded-md border border-solid border-line bg-surface-raised text-muted hover:text-woot-500 hover:border-woot-400;
}

.template__media-row {
  @apply flex items-start;
}

.media-helper {
  @apply text-xs text-subtle mt-1 mb-0;
}

.media-success {
  @apply text-xs text-green-700 dark:text-green-400 mt-1 mb-0;
}

.media-error {
  @apply text-xs text-red-700 dark:text-red-400 mt-1 mb-0;
}

footer {
  @apply flex justify-end mt-4 border-t border-line-faint pt-4;

  button {
    @apply ml-2.5;
  }
}

.error {
  @apply bg-red-100 dark:bg-red-900/20 rounded-md text-red-800 dark:text-red-400 p-2.5 text-center mt-2 text-sm;
}

.template-input {
  @apply w-full bg-surface-raised text-muted border border-line rounded-md p-2 text-sm resize-none focus:outline-none focus:border-woot-500;
}
</style>
